import { useState, useEffect, useMemo, useRef } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowUpRight, ChevronLeft, ChevronRight, CircleHelp, CreditCard, FileSignature,
  FileText, GraduationCap, Home, LogOut, Menu, MessageCircleMore, RefreshCw,
  MoreHorizontal, Files, UserRound, X, Search,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { StudentPortalProvider, useStudentPortal } from '../contexts/StudentPortalContext';
import PortalNotifications from './PortalNotifications';
import { useI18n } from '../contexts/I18nContext';
import Logo from '../assets/1.png';
import './StudentPortalLayout.css';

const copy = {
  en: {
    dashboard: 'Overview',
    application: 'My Application',
    universities: 'Universities',
    payments: 'Payments & Fees',
    documents: 'Documents',
    contract: 'My Contract',
    chat: 'Consultant Chat',
    profile: 'My Profile',
    help: 'Help & Support',
    logout: 'Log out',
    loggingOut: 'Logging out…',
    workspace: 'Student Workspace',
    retry: 'Try again',
    loadError: 'We could not open your workspace.',
    searchPlaceholder: 'Search universities, documents, payments...',
    searchHeading: 'Quick Search & Navigation',
    quickLinks: 'Quick Links',
    noResults: 'No matches found',
    pressEsc: 'ESC to close',
    nextStep: 'Next Step',
    more: 'More',
  },
  ar: {
    dashboard: 'نظرة عامة',
    application: 'طلبي',
    universities: 'الجامعات',
    payments: 'المدفوعات والمستحقات',
    documents: 'المستندات',
    contract: 'العقد',
    chat: 'محادثة المستشار',
    profile: 'ملفي الشخصي',
    help: 'المساعدة والدعم',
    logout: 'تسجيل الخروج',
    loggingOut: 'جارٍ تسجيل الخروج…',
    workspace: 'مساحة الطالب',
    retry: 'إعادة المحاولة',
    loadError: 'تعذر فتح مساحة الطالب.',
    searchPlaceholder: 'ابحث في الجامعات، المستندات، المدفوعات...',
    searchHeading: 'البحث السريع والتنقل',
    quickLinks: 'روابط سريعة',
    noResults: 'لم يتم العثور على نتائج',
    pressEsc: 'ESC للإغلاق',
    nextStep: 'خطوتك التالية',
    more: 'المزيد',
  },
};

function PortalFrame() {
  const { language, setLanguage, isRTL } = useI18n();
  const { user, logout } = useAuth();
  const { workspace, loading, error, refresh } = useStudentPortal();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loggingOut, setLoggingOut] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const wasMenuOpen = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const t = copy[language];
  const Arrow = isRTL ? ChevronRight : ChevronLeft;

  const docsPending = Math.max(
    0,
    (workspace?.summaries.documents?.total || 0) - (workspace?.summaries.documents?.approved || 0)
  );
  const paymentsPending = workspace?.summaries.additional_payments?.action_required || 0;

  const nav = [
    { to: '/app', label: t.dashboard, icon: Home, end: true },
    { to: '/app/application', label: t.application, icon: FileText },
    { to: '/app/universities', label: t.universities, icon: GraduationCap },
    {
      to: '/app/documents',
      label: t.documents,
      icon: Files,
      badge: docsPending > 0 ? docsPending : undefined,
    },
    {
      to: '/app/payments',
      label: t.payments,
      icon: CreditCard,
      badge: paymentsPending > 0 ? paymentsPending : undefined,
    },
    ...(workspace?.capabilities.view_contract
      ? [{ to: '/app/contract', label: t.contract, icon: FileSignature }]
      : []),
    { to: '/app/chat', label: t.chat, icon: MessageCircleMore },
  ];

  const bottomNav = [
    { to: '/app', label: t.dashboard, icon: Home, end: true },
    { to: '/app/application', label: t.application, icon: FileText },
    {
      to: '/app/documents',
      label: t.documents,
      icon: Files,
      badge: docsPending > 0 ? docsPending : undefined,
    },
    {
      to: '/app/payments',
      label: language === 'ar' ? 'المدفوعات' : 'Payments',
      icon: CreditCard,
      badge: paymentsPending > 0 ? paymentsPending : undefined,
    },
  ];

  const nextRoute = workspace?.next_route.startsWith('/onboarding')
    ? '/app/onboarding'
    : (workspace?.next_route || '/app');

  const nextActionArabic: Record<string, string> = {
    select_country: 'اختر وجهتك الدراسية',
    continue_setup: 'أكمل إعداد طلبك',
    complete_contract: 'أكمل بيانات العقد وراجعه',
    accept_quote: 'وافق على عرض السعر',
    pay_initial_deposit: 'أرسل الدفعة الأولى',
    wait_for_payment_review: 'تابع مراجعة الدفع',
    view_application_summary: 'راجع ملخص طلبك',
    view_next_application_task: 'تابع خطوتك التالية',
  };

  const nextActionTitle = workspace
    ? (language === 'ar'
      ? (nextActionArabic[workspace.next_action.code.toLowerCase()] || workspace.next_action.title)
      : workspace.next_action.title)
    : '';

  const searchableItems = useMemo(() => [
    {
      id: 'docs',
      title: language === 'ar' ? 'المستندات وقائمة الرفع' : 'Documents & Upload Checklist',
      description: language === 'ar' ? 'رفع وفحص الجواز والشهادات' : 'Upload and verify passport & certificates',
      category: language === 'ar' ? 'المستندات' : 'Documents',
      icon: Files,
      to: '/app/documents',
      keywords: 'documents files passport certificate مستندات ملفات جواز شهادة رفع أوراق',
    },
    {
      id: 'payments',
      title: language === 'ar' ? 'المدفوعات وجدول الأقساط' : 'Payments & Fee Schedule',
      description: language === 'ar' ? 'سداد الدفعات وإرفاق الإيصالات والفواتير' : 'Pay instalments and attach transfer receipts',
      category: language === 'ar' ? 'المالية' : 'Finance',
      icon: CreditCard,
      to: '/app/payments',
      keywords: 'payments fees invoices receipt دفع مدفوعات فواتير أقساط رسوم إيصال تحويل',
    },
    {
      id: 'application',
      title: language === 'ar' ? 'تفاصيل الطلب الرئيسي والجدول الزمني' : 'Main Application Details & Timeline',
      description: language === 'ar' ? 'متابعة مراحل الطلب وتحديثات المستشار' : 'Track application stages and consultant updates',
      category: language === 'ar' ? 'الطلب' : 'Application',
      icon: FileText,
      to: '/app/application',
      keywords: 'application status tracking progress طلب حالة متابعة مراحل رحلة',
    },
    {
      id: 'universities',
      title: language === 'ar' ? 'الجامعات والتخصصات المرشحة' : 'Universities & Programs',
      description: language === 'ar' ? 'استعراض الجامعات والبرامج الأكاديمية' : 'Browse universities & study programs',
      category: language === 'ar' ? 'الجامعات' : 'Universities',
      icon: GraduationCap,
      to: '/app/universities',
      keywords: 'universities programs majors italy romania جامعات تخصصات برامج كليات إيطاليا رومانيا قبول',
    },
    {
      id: 'chat',
      title: language === 'ar' ? 'محادثة المستشار الأكاديمي' : 'Consultant Direct Chat',
      description: language === 'ar' ? 'تواصل مباشر مع مستشارك المخصص' : 'Chat directly with your assigned advisor',
      category: language === 'ar' ? 'الدعم' : 'Support',
      icon: MessageCircleMore,
      to: '/app/chat',
      keywords: 'chat message consultant advisor support محادثة شات رسائل مستشار دعم تواصل',
    },
    {
      id: 'profile',
      title: language === 'ar' ? 'الملف الشخصي وإعدادات الحساب' : 'My Student Profile & Settings',
      description: language === 'ar' ? 'إدارة البيانات وكلمة المرور' : 'Manage personal details and password',
      category: language === 'ar' ? 'الحساب' : 'Account',
      icon: UserRound,
      to: '/app/profile',
      keywords: 'profile account settings password ملف شخصي حساب إعدادات كلمة المرور',
    },
  ], [language]);

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return searchableItems;
    return searchableItems.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.category.toLowerCase().includes(q) ||
        item.keywords.toLowerCase().includes(q)
    );
  }, [searchQuery, searchableItems]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setSearchOpen(false);
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
    }
  }, [searchOpen]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen) {
      wasMenuOpen.current = true;
      const frame = requestAnimationFrame(() => closeButtonRef.current?.focus());
      return () => cancelAnimationFrame(frame);
    }
    if (wasMenuOpen.current) {
      wasMenuOpen.current = false;
      const active = document.activeElement;
      if (!active || active === document.body || sidebarRef.current?.contains(active)) {
        menuButtonRef.current?.focus();
      }
    }
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);
  const handleLogout = async () => {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login', { replace: true });
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSelectSearchItem = (to: string) => {
    setSearchOpen(false);
    navigate(to);
  };

  if (loading) {
    return (
      <div className="portal-loading" role="status" aria-label="Loading student workspace">
        <img src={Logo} alt="EduNasser" />
        <span className="portal-loader" />
      </div>
    );
  }

  if (error || !workspace) {
    return (
      <div className="portal-error-state">
        <div className="portal-error-card">
          <div className="portal-error-icon"><RefreshCw size={25} /></div>
          <h1>{t.loadError}</h1>
          <p>{error}</p>
          <button className="portal-button portal-button-primary" onClick={() => void refresh()}>{t.retry}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="portal-root" dir={isRTL ? 'rtl' : 'ltr'}>
      <aside id="portal-sidebar" ref={sidebarRef} className={`portal-sidebar ${menuOpen ? 'is-open' : ''}`} aria-label="Student navigation">
        <div className="portal-brand">
          <img src={Logo} alt="EduNasser" />
          <button
            ref={closeButtonRef}
            type="button"
            className="portal-icon-button portal-mobile-close"
            onClick={closeMenu}
            aria-label={isRTL ? 'إغلاق القائمة' : 'Close menu'}
          >
            <X size={20} />
          </button>
        </div>

        <NavLink to="/app/profile" onClick={closeMenu} className="portal-sidebar-user">
          <span className="portal-avatar">
            {user?.name?.trim().charAt(0).toUpperCase() || 'S'}
          </span>
          <span className="portal-sidebar-user-meta">
            <strong>{user?.name}</strong>
            <small><bdi>{user?.email}</bdi></small>
          </span>
          {isRTL
            ? <ChevronLeft size={16} className="portal-sidebar-user-arrow" />
            : <ChevronRight size={16} className="portal-sidebar-user-arrow" />}
        </NavLink>

        <nav className="portal-nav">
          {nav.map(({ to, label, icon: Icon, badge, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              onClick={closeMenu}
              className={({ isActive }) => `portal-nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={20} strokeWidth={1.9} />
              <span>{label}</span>
              {!!badge && <b className="portal-nav-badge">{badge}</b>}
            </NavLink>
          ))}
        </nav>

        <div className="portal-sidebar-bottom">
          <NavLink
            to="/app/profile"
            end
            onClick={closeMenu}
            className={({ isActive }) => `portal-nav-item ${isActive ? 'active' : ''}`}
          >
            <UserRound size={20} />
            <span>{t.profile}</span>
          </NavLink>
          <NavLink
            to="/app/help"
            onClick={closeMenu}
            className={({ isActive }) => `portal-nav-item ${isActive ? 'active' : ''}`}
          >
            <CircleHelp size={20} />
            <span>{t.help}</span>
          </NavLink>
          <button
            type="button"
            className="portal-nav-item portal-logout"
            onClick={() => void handleLogout()}
            disabled={loggingOut}
          >
            <LogOut size={20} />
            <span>{loggingOut ? t.loggingOut : t.logout}</span>
          </button>
        </div>
      </aside>

      {menuOpen && <button type="button" className="portal-overlay" onClick={closeMenu} tabIndex={-1} aria-label={isRTL ? 'إغلاق القائمة' : 'Close menu'} />}

      <div className="portal-stage">
        <header className="portal-topbar">
          <div className="portal-topbar-start">
            <button
              ref={menuButtonRef}
              type="button"
              className="portal-icon-button portal-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-label={isRTL ? 'فتح القائمة' : 'Open menu'}
              aria-controls="portal-sidebar"
              aria-expanded={menuOpen}
            >
              <Menu size={20} />
            </button>
            <div className="portal-mobile-logo">
              <img src={Logo} alt="EduNasser" />
            </div>

            <button className="portal-icon-button portal-search-trigger" onClick={() => setSearchOpen(true)} aria-label={t.searchPlaceholder} title={t.searchPlaceholder}>
              <Search size={18} />
            </button>
          </div>

          <div className="portal-topbar-actions">
            <button
              className="portal-topbar-next"
              onClick={() => navigate(nextRoute)}
              title={`${t.nextStep}: ${nextActionTitle}`}
            >
              <span className="portal-topbar-next-badge">
                <ArrowUpRight size={13} />
                {t.nextStep}
              </span>
              <strong className="portal-topbar-next-text">{nextActionTitle}</strong>
            </button>

            <PortalNotifications />

            <button
              className="portal-icon-button portal-language"
              onClick={() => setLanguage(language === 'ar' ? 'en' : 'ar')}
              title={language === 'ar' ? 'Switch to English' : 'التحويل إلى العربية'}
              aria-label="Toggle language"
            >
              {language === 'ar' ? 'EN' : 'ع'}
            </button>

            <div className="portal-topbar-divider" aria-hidden="true" />

            <div className="portal-user" onClick={() => navigate('/app/profile')} role="button" tabIndex={0}>
              <span className="portal-avatar">
                {user?.name?.trim().charAt(0).toUpperCase() || 'S'}
              </span>
              <span className="portal-user-meta">
                <strong>{user?.name}</strong>
                <small>{user?.email}</small>
              </span>
              <Arrow size={14} className="portal-user-arrow" />
            </div>
          </div>
        </header>

        {searchOpen && (
          <div className="portal-search-modal-backdrop" onClick={() => setSearchOpen(false)}>
            <div className="portal-search-modal" onClick={(e) => e.stopPropagation()}>
              <div className="portal-search-modal-head">
                <Search size={20} className="portal-search-modal-icon" />
                <input
                  ref={searchInputRef}
                  type="text"
                  className="portal-search-modal-input"
                  placeholder={t.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="portal-search-clear-btn"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
                <span className="portal-search-esc">{t.pressEsc}</span>
              </div>

              <div className="portal-search-modal-body">
                <div className="portal-search-section-title">
                  {searchQuery ? t.searchHeading : t.quickLinks}
                </div>

                {filteredItems.length > 0 ? (
                  <div className="portal-search-list">
                    {filteredItems.map((item) => {
                      const ItemIcon = item.icon;
                      return (
                        <button
                          key={item.id}
                          className="portal-search-item"
                          onClick={() => handleSelectSearchItem(item.to)}
                        >
                          <div className="portal-search-item-icon">
                            <ItemIcon size={18} />
                          </div>
                          <div className="portal-search-item-info">
                            <div className="portal-search-item-top">
                              <strong>{item.title}</strong>
                              <span className="portal-search-category-pill">{item.category}</span>
                            </div>
                            <small>{item.description}</small>
                          </div>
                          <Arrow size={16} className="portal-search-item-arrow" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="portal-search-empty">
                    <Search size={32} />
                    <p>{t.noResults}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <main className="portal-main" key={location.pathname}>
          <Outlet />
        </main>
      </div>

      <nav className="portal-bottom-nav" aria-label="Mobile navigation">
        {bottomNav.map(({ to, label, icon: Icon, badge, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `portal-bottom-nav-item ${isActive ? 'active' : ''}`}
          >
            <div className="portal-bottom-nav-icon-wrapper">
              <Icon size={20} />
              {!!badge && <span className="portal-bottom-badge">{badge}</span>}
            </div>
            <span>{label}</span>
          </NavLink>
        ))}
        <button
          type="button"
          className={`portal-bottom-nav-item ${menuOpen ? 'active' : ''}`}
          onClick={() => setMenuOpen(true)}
          aria-controls="portal-sidebar"
          aria-expanded={menuOpen}
        >
          <div className="portal-bottom-nav-icon-wrapper">
            <MoreHorizontal size={20} />
          </div>
          <span>{t.more}</span>
        </button>
      </nav>
    </div>
  );
}

export default function StudentPortalLayout() {
  return (
    <StudentPortalProvider>
      <PortalFrame />
    </StudentPortalProvider>
  );
}
